<template>
    <div>
        <v-container grid-list-xs>
            <v-sheet class="pa-5" elevation="3">
                <Form @submit="submit">
                    <v-row class="header">
                        <v-col>
                            <PageHeader>
                                <template #header>
                                    <h4>Congratulations on your approval!</h4>
                                </template>
                                <template #subheader>
                                    <div>
                                        {{ $t("additional-contact-info.sub-header", { providerKey: providerKey }) }}
                                    </div>
                                </template>
                            </PageHeader>
                        </v-col>
                    </v-row>

                    <div v-if="mpfCheck" class="pt-sm-5">
                        <v-subheader class="input-title">{{ $t("additionalInfo.payment-frequency") }}</v-subheader>
                        <p>{{ $t("additionalInfo.payment-frequency-description") }}</p>

                        <v-row dense>
                            <v-col>
                                <AppField
                                    v-if="fields['primaryIncomePayFrequency']"
                                    :application="application"
                                    :field="fields['primaryIncomePayFrequency']"
                                    type="select"
                                    label-code="field.payFrequency"
                                    :form-settings="formSettings"
                                    append-icon="$chevronDown"
                                >
                                </AppField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <AppField
                                    v-if="fields['primaryIncomePreviousPayDate']"
                                    :application="application"
                                    :field="fields['primaryIncomePreviousPayDate']"
                                    label-code="field.previousPayDate"
                                    type="date"
                                    :form-settings="formSettings"
                                    :max="today.toDate()"
                                    :min="minPreviousDateThreshold"
                                />
                            </v-col>
                        </v-row>
                    </div>

                    <i18n
                        v-if="paymentFrequency && primaryIncomePreviousPayDate && previousPayDateObj"
                        tag="p"
                        :path="`hfd_standard.additional-contact-info.pay-frequency.${paymentFrequency}.blurb`"
                    >
                        <template #intro>
                            <i18n tag="b" path="hfd_standard.additional-contact-info.pay-frequency.intro" />
                        </template>

                        <template #schedule>
                            <i18n
                                tag="b"
                                :path="`hfd_standard.additional-contact-info.pay-frequency.${paymentFrequency}.schedule`"
                            />
                        </template>

                        <template #lastPayDate>
                            <b>{{ lastPayDate }}</b>
                        </template>

                        <template #dayOfTheWeek>
                            <b>{{ dayOfTheWeek }}</b>
                        </template>

                        <template #day1>
                            <b>{{ day1 }}</b>
                        </template>

                        <template #day2>
                            <b>{{ day2 }}</b>
                        </template>
                    </i18n>

                    <div v-min-children="2">
                        <v-subheader class="input-title">Contact</v-subheader>
                        <v-row v-min-children="1" dense>
                            <AppField
                                v-if="fields['primaryMobilePhone']"
                                :application="application"
                                :field="fields['primaryMobilePhone']"
                                label-code="field.mobilePhone"
                                :form-settings="formSettings"
                                :required="true"
                                type="phone"
                                messages="By entering your mobile number, you are providing your express consent to communicate with you via direct and automated calls and text messages. Mobile, message, and data rates may apply."
                            />
                        </v-row>
                        <v-row v-min-children="1" dense>
                            <AppField
                                v-if="fields['primaryEmail']"
                                :application="application"
                                :field="fields['primaryEmail']"
                                label-code="field.email"
                                :form-settings="formSettings"
                                :required="true"
                                type="email"
                                messages="A valid email is required to accept the offer."
                            />
                        </v-row>
                    </div>
                    <div>
                        <v-row>
                            <v-col>
                                <v-checkbox v-model="termsCheckBox">
                                    <template #label>
                                        <div>
                                            By checking this box you agree to HFD's
                                            <a
                                                href="https://gohfd.com/esign-consent-and-agreement"
                                                target="_blank"
                                                @click.stop
                                                >E-Sign Consent And Agreement</a
                                            >,
                                            <a href="https://gohfd.com/privacy-notice" target="_blank" @click.stop
                                                >Privacy Notice</a
                                            >,
                                            <a href="https://gohfd.com/usa-patriot-act" target="_blank" @click.stop
                                                >USA Patriot Act</a
                                            >
                                        </div>
                                    </template>
                                </v-checkbox>
                            </v-col>
                        </v-row>
                    </div>

                    <v-card-actions class="pb-12 px-0">
                        <v-row justify="space-between">
                            <v-col class="mt-6">
                                <BackButton class="mr-4" color="primary" rounded :to="backPath">
                                    {{ $t("action.back") }}
                                </BackButton>
                            </v-col>
                            <v-col class="text-right mt-6">
                                <SubmitButton class="secondary" :disabled="isDisabled" :submitting="submitting">
                                    {{ $t("action.continue") }}
                                </SubmitButton>
                            </v-col>
                        </v-row>
                    </v-card-actions>
                </Form>
                <ResponsiveImage :image-url="footerLogo" width="100px" class="footer-logo-center" />
            </v-sheet>
        </v-container>
    </div>
</template>

<script>
    import { applicationState, fieldState, sessionState, providerState } from "@/state"
    import { indexBy } from "@/util/array"
    import ProviderState from "@/state/ProviderState"
    import Providers from "@/model/provider/Providers"
    import ProviderKey from "@/model/provider/ProviderKey"
    import ApplyMode from "@/model/apply/ApplyMode"
    import { applyService } from "@/service"
    import { mapState } from "vuex"
    import { payDateNotValidForFrequency } from "@/util/validation"
    import dayjs from "dayjs"
    import advancedFormat from "dayjs/plugin/advancedFormat"
    import { PayFrequency } from "@/model/apply/PayFrequencies"
    dayjs.extend(advancedFormat)

    export default {
        data() {
            return {
                application: applicationState.application,
                fields: indexBy(fieldState.fields, "id"),
                termsCheckBox: false,
                formSettings: {
                    readOnly: false,
                },
                today: dayjs(),
                providerInfo: ProviderState.providers,
                submitting: false,
                session: sessionState.session,
                providerKey: sessionState.providerKey,
                validations: {
                    primaryIncomePreviousPayDate: {
                        payDateNotValidForFrequency: this.calcPayDateNotValidForFrequency,
                    },
                },
            }
        },

        computed: {
            ...mapState("validation", {
                fieldErrors: state => state.data.validationErrorResponse.fieldErrors,
            }),

            primaryIncomePreviousPayDate() {
                return this.application.primary.income.previousPayDate
            },

            minPreviousDateThreshold() {
                return this.today.subtract(45, "day").toDate()
            },

            paymentFrequency() {
                return this.application.primary.income.payFrequency
            },

            mpfCheck() {
                return providerState.getProviderProperties(sessionState.providerKey).employmentPayDetailsEnabled
            },

            backPath() {
                if (this.mpfCheck) {
                    return `/providers/${sessionState.providerKey}/paymentPlans`
                } else {
                    return `/providers/${sessionState.providerKey}/paymentPlanReview`
                }
            },

            previousPayDateObj() {
                const prevDate = this.primaryIncomePreviousPayDate
                if (!prevDate) return null
                const date = dayjs(prevDate)
                if (!date.isValid()) return null

                return date.isAfter(this.today) || date.isBefore(this.minPreviousDateThreshold) ? null : date
            },

            dayOfTheWeek() {
                if (!this.previousPayDateObj) return ""
                if (this.paymentFrequency === PayFrequency.WEEKLY || this.paymentFrequency === PayFrequency.BI_WEEKLY) {
                    return this.previousPayDateObj.format("dddd")
                }
                return ""
            },

            lastPayDate() {
                if (!this.previousPayDateObj) return ""
                if (this.paymentFrequency === PayFrequency.MONTHLY) {
                    if (Number(dayjs(this?.previousPayDateObj).format("D")) >= 29) {
                        return "28th"
                    }
                    return this.previousPayDateObj.format("Do")
                }
                return ""
            },

            day1() {
                if (!this.previousPayDateObj) return ""
                if (this.paymentFrequency !== PayFrequency.SEMI_MONTHLY) return ""

                const date = this.previousPayDateObj
                const dayOfMonth = Number(date.format("D"))

                if (dayOfMonth <= 15) {
                    // if last pay date between 1st and 15th
                    return date.format("Do")
                }
                if (dayOfMonth === 30 || dayOfMonth === 31) {
                    // if last pay date is the 30th or 31st
                    return "15th"
                }
                // if between 16th and 29th
                return date.subtract(15, "day").format("Do")
            },

            day2() {
                if (!this.previousPayDateObj) return ""
                if (this.paymentFrequency !== PayFrequency.SEMI_MONTHLY) return ""

                const date = this.previousPayDateObj
                const dayOfMonth = Number(date.format("D"))

                if (dayOfMonth <= 15) {
                    // if last pay date between 1st and 15th
                    return date.add(15, "day").format("Do")
                }
                if (dayOfMonth === 30 || dayOfMonth === 31) {
                    // if last pay date is the 30th or 31st
                    return date.endOf("month").format("Do")
                }
                // if between 16th and 29th
                return date.format("Do")
            },

            footerLogo() {
                const response = ProviderState.getPreAuthResponse()
                return Providers[ProviderKey.HFD_STANDARD]?.footerLogo[response.providerProductName]
            },

            isDisabled() {
                if (this.mpfCheck) {
                    return (
                        !this.application.primary.income.payFrequency ||
                        !this.application.primary.income.previousPayDate ||
                        !this.application.primary.mobilePhone ||
                        !this.application.primary.email ||
                        !this.termsCheckBox
                    )
                }
                return !this.application.primary.mobilePhone || !this.application.primary.email || !this.termsCheckBox
            },
        },

        methods: {
            calcPayDateNotValidForFrequency(value, parentVM) {
                return payDateNotValidForFrequency(value, parentVM)
            },

            async submit() {
                try {
                    this.submitting = true
                    applicationState.application = this.application

                    await applyService.patch(
                        sessionState.providerKey,
                        { application: this.application },
                        ApplyMode.PREQUALIFY
                    )

                    if (this.mpfCheck) {
                        const plans = await applyService.getPaymentPlans(sessionState.providerKey, {
                            amountCents: ProviderState.getPreAuthResponse().approvedAmountCents,
                        })
                        providerState.setPaymentPlans(sessionState.providerKey, plans.paymentPlans)

                        await applyService.selectPaymentPlan(sessionState.providerKey, {
                            providerPlanCode: applicationState.selectedPaymentPlan.providerPlanCode,
                            financeAmountCents: applicationState.application.purchaseAmountCents,
                            termMonths: applicationState.selectedPaymentPlan.termMonths,
                        })

                        const updateSelectedPlan = plans.paymentPlans.filter(
                            plan =>
                                plan.providerPlanId === applicationState.selectedPaymentPlan.providerPlanId &&
                                plan.providerPlanCode === applicationState.selectedPaymentPlan.providerPlanCode
                        )

                        applicationState.selectedPaymentPlan =
                            updateSelectedPlan.length === 1
                                ? updateSelectedPlan[0]
                                : applicationState.selectedPaymentPlan

                        await this.$router.push(`/providers/${sessionState.providerKey}/mpfPaymentPlanReview`)
                    } else {
                        const paymentPlan = applicationState.selectedPaymentPlan
                        if (paymentPlan.downPaymentCents > 0) {
                            await this.$router.push(`/providers/${sessionState.providerKey}/downpayment`)
                        } else {
                            await this.$router.push(`/providers/${sessionState.providerKey}/loanPayment`)
                        }
                    }
                } finally {
                    this.submitting = false
                }
            },
        },
    }
</script>
<style lang="scss" scoped>
    .header {
        min-height: 120px !important;
    }
    .tab {
        margin-left: 40px;
        margin-right: 40px;
    }
</style>
